/**
 * Jotai atoms for the schedule-dialog's client-only state (spec 3): the browser-local
 * working override log + its committed baseline, the point selection, the
 * expand/collapse state, and the toolbar filters. All of this is session state — it
 * never persists and is discarded on leave (by design, per PM). The only persisted
 * state is the existing `configurations.slot_overrides` written by the Calculate flow.
 */

import { atom } from 'jotai';
import { hasUncommittedEdits } from './edit-ops';
import { EMPTY_SELECTION, type SelectionSet } from './selection-model';
import type {
  PhaseKey,
  ScheduleOverrideEntry,
  ScheduleViewMode,
} from './types';

// Re-export the seam types so consumers import contract + client types from one place.
export type {
  MergedDate,
  MilestoneKey,
  ScheduleDialogPayload,
  ScheduleOverrideEntry,
  ScheduleRow,
} from './types';

/** Chart vs. Table view (the Table body is the next story). */
export const viewModeAtom = atom<ScheduleViewMode>('chart');

/** The browser-local working copy of the override ops-log. */
export const workingEntriesAtom = atom<ScheduleOverrideEntry[]>([]);

/** The committed baseline the working copy was seeded from (for dirtiness). */
export const baselineEntriesAtom = atom<ScheduleOverrideEntry[]>([]);

/** True when the working copy differs from its committed baseline. */
export const hasUncommittedEditsAtom = atom((get) =>
  hasUncommittedEdits(get(workingEntriesAtom), get(baselineEntriesAtom))
);

/** Selected milestone points as a `Set<slotId|field>` (ids + count only). */
export const selectionAtom = atom<SelectionSet>(EMPTY_SELECTION);

/**
 * Expanded numeric area-group ids. The dialog seeds this to every group on open (see
 * ScheduleDialog's payload effect), so groups default to fully expanded; the empty-set
 * initial value only holds before the first payload arrives.
 */
export const expandedGroupsAtom = atom<Set<number>>(new Set<number>());

/** Whether the "Unscheduled" pseudo-group is expanded (seeded on with the groups on open). */
export const unscheduledExpandedAtom = atom<boolean>(false);

/** Future-locations-only toggle (default ON). */
export const futureOnlyAtom = atom<boolean>(true);

/** Free-text search (well name / API / slot id). */
export const searchQueryAtom = atom<string>('');

/** Active phase filter, or null for all phases. */
export const phaseFilterAtom = atom<PhaseKey | null>(null);

/**
 * Opens the "View property schedule" (Gantt) dialog from OUTSIDE the Case-management
 * panel — the Properties grid's per-date-cell schedule affordance sets this true, and
 * `ViewPropertySchedule` (already mounted in the panel, hidden or not) opens the dialog
 * in response (INS-12497). Session-only, like the rest of this file; reset to false when
 * the dialog closes.
 */
export const scheduleDialogOpenAtom = atom<boolean>(false);

/**
 * A property to pre-filter the schedule on its NEXT open. The Properties grid's per-date-cell
 * affordance sets this to the clicked row's slot id, so the dialog opens focused on that well
 * (INS-12497). ONE-SHOT: the dialog seeds `searchQueryAtom` from it on open and clears it, so a
 * later open from the Case-management panel button starts unfiltered. Null = no focus.
 */
export const scheduleFocusSearchAtom = atom<string | null>(null);
